import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  LuArrowLeft,
  LuArrowRight,
  LuClock,
  LuHandCoins,
  LuUtensils,
  LuWallet,
} from "react-icons/lu";
import { DIR, isEnglish, t } from "../../i18n";
import { useAuthContext } from "../../contexts/AuthContext";
import { axios } from "../../lib/axios";
import { formatIQD, formatPayroll } from "../../lib/currency";
import { currentMonth, currentWeekStart } from "../../lib/week";
import { navItemsFor } from "../Layout/navItems";
import Alerts from "./Alerts";

// One number on the home page; the tile opens the page where it comes from.
const SummaryTile = ({ to, Icon, label, value, note, tone = "brand" }) => {
  const tones = {
    brand: "bg-brand-100 text-brand-700",
    warn: "bg-amber-100 text-amber-700",
    good: "bg-emerald-100 text-emerald-700",
  };
  return (
    <li>
      <Link to={to} className="card-interactive card-in flex h-full flex-col gap-2 p-4">
        <span className="flex items-center gap-2 text-sm font-medium text-slate-600">
          <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tones[tone]}`}>
            <Icon aria-hidden="true" />
          </span>
          {label}
        </span>
        <span className="break-words text-xl font-bold tabular-nums text-slate-900 sm:text-2xl">
          {value}
        </span>
        <span className="text-sm text-slate-600">{note}</span>
      </Link>
    </li>
  );
};

// This week's pay, what is still unpaid, what employees owe and this month's food, from one request.
const Summary = () => {
  const [state, setState] = useState({ status: "loading", data: null });

  const load = useCallback((signal) => {
    setState({ status: "loading", data: null });
    axios
      .get("/summary", {
        params: { week_start: currentWeekStart(), month: currentMonth() },
        signal,
      })
      .then(({ data }) => {
        if (!data?.week || !data?.deductions || !data?.food) throw new Error("bad shape");
        setState({ status: "ok", data });
      })
      .catch((err) => {
        if (err.code === "ERR_CANCELED") return; // left the page or asked again
        setState({ status: "error", data: null });
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const { status, data } = state;
  const week = data?.week;
  const waiting = week ? week.employees - week.paid_employees : 0;

  return (
    <section aria-labelledby="summary-title" aria-busy={status === "loading"} className="mb-6">
      <h2 id="summary-title" className="mb-3 text-lg font-semibold text-brand-800">
        {t("ملخص سريع")}
      </h2>
      {status === "loading" && (
        <ul className="grid grid-cols-2 gap-3 xl:grid-cols-4" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <li key={i} className="card h-28 animate-pulse" />
          ))}
        </ul>
      )}
      {status === "error" && (
        <div role="alert" className="card flex flex-wrap items-center gap-3 p-4 text-slate-700">
          <span>{t("تعذر تحميل الملخص.")}</span>
          <button
            type="button"
            onClick={() => load()}
            className="rounded-lg border border-slate-300 bg-card px-3 py-1.5 font-medium hover:bg-slate-100"
          >
            {t("إعادة المحاولة")}
          </button>
        </div>
      )}
      {status === "ok" && (
        <ul className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <SummaryTile
            to="/salaries"
            Icon={LuWallet}
            label={t("رواتب هذا الأسبوع")}
            value={formatPayroll(week.total)}
            note={
              week.employees === 0 ? (
                t("لا توجد سجلات لهذا الأسبوع")
              ) : (
                <>
                  {t("{n} موظف", { n: week.employees })}
                  <span dir="ltr" className="block whitespace-nowrap tabular-nums">
                    {week.start.slice(5)} – {week.end.slice(5)}
                  </span>
                </>
              )
            }
          />
          <SummaryTile
            to="/salaries"
            Icon={week.unpaid > 0 ? LuClock : LuWallet}
            tone={
              week.employees > 0 && week.unpaid === 0 ? "good" : week.unpaid > 0 ? "warn" : "brand"
            }
            label={t("غير مدفوع")}
            value={formatPayroll(week.unpaid)}
            note={
              week.employees === 0
                ? "—"
                : week.unpaid === 0
                  ? t("تم دفع الجميع")
                  : t("{n} موظف لم يستلموا الراتب", { n: waiting })
            }
          />
          <SummaryTile
            to="/Deduction-section"
            Icon={LuHandCoins}
            tone={data.deductions.total > 0 ? "warn" : "brand"}
            label={t("استقطاعات على الموظفين")}
            value={formatPayroll(data.deductions.total)}
            note={
              data.deductions.employees > 0
                ? t("{n} موظف عليهم استقطاع", { n: data.deductions.employees })
                : t("لا يوجد استقطاع")
            }
          />
          <SummaryTile
            to="/food"
            Icon={LuUtensils}
            label={t("طعام هذا الشهر")}
            value={formatIQD(data.food.total)}
            note={
              data.food.items > 0
                ? t("{n} عنصر", { n: data.food.items })
                : t("لا يوجد طعام هذا الشهر")
            }
          />
        </ul>
      )}
    </section>
  );
};

// Landing page after sign-in: one card per page with a short description; the card opens the page.
const HomePage = () => {
  const { isOwner } = useAuthContext();
  const pages = navItemsFor(isOwner).filter((item) => item.description);
  const Arrow = isEnglish ? LuArrowRight : LuArrowLeft;

  return (
    <div dir={DIR}>
      <Summary />
      <Alerts />
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-brand-800">
          {t("اختر القسم الذي تريد العمل عليه")}
        </h2>
      </div>
      <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {pages.map(({ path, label, Icon, description }) => (
          <li key={path}>
            <Link
              to={path}
              className="card-interactive card-in group flex h-full flex-col gap-3 p-5"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-100 text-2xl text-brand-700 transition-colors group-hover:bg-brand-600 group-hover:text-white">
                <Icon aria-hidden="true" />
              </span>
              <span className="text-lg font-bold text-slate-900">{label}</span>
              <span className="flex-1 text-sm leading-relaxed text-slate-600">{description}</span>
              <span className="inline-flex items-center gap-2 text-sm font-medium text-brand-700">
                {t("فتح الصفحة")}
                <Arrow aria-hidden="true" />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default HomePage;
